import { useState, useEffect, useCallback, useRef } from 'react'
import dotloopApi from '../services/dotloopApi'
import { AuthStatus } from '../models/DotloopTypes'

const defaultAuthStatus: AuthStatus = {
  authenticated: false,
  hasRefreshToken: false,
  expiresAt: null,
  needsReauth: false,
  validationError: null,
  dotloopEmail: null,
  dotloopAccountId: null
}

export default function useDotloopAuth(fubUserId?: number) {
  const [authStatus, setAuthStatus] = useState<AuthStatus>(defaultAuthStatus)
  const [loading, setLoading] = useState(true)
  
  // Use a ref to track if we're expecting an auth callback
  // This helps prevent race conditions with multiple auth checks
  const authPendingRef = useRef(false)
  const authRequestIdRef = useRef<string | null>(null)
  const authTransferInFlightRef = useRef(false)

  // Stable checkAuth function using useCallback
  const checkAuth = useCallback(async () => {
    // #region agent log
    console.log('[DEBUG:C,E] checkAuth called', {authPending:authPendingRef.current,ts:Date.now()});
    // #endregion
    setLoading(true)
    try {
      const status = await dotloopApi.checkAuthStatus()
      // #region agent log
      console.log('[DEBUG:C,D] checkAuth got status', {authenticated:status.authenticated,email:status.dotloopEmail,needsReauth:status.needsReauth,ts:Date.now()});
      // #endregion
      setAuthStatus(status)
      return status
    } catch (error) {
      console.error('Error checking auth:', error)
      const errorStatus = {
        ...defaultAuthStatus,
        needsReauth: true,
        validationError: error instanceof Error ? error.message : 'Failed to check auth'
      }
      setAuthStatus(errorStatus)
      return errorStatus
    } finally {
      setLoading(false)
    }
  }, [])

  // Handle auth transfer token from popup (for iframe contexts where cookies don't work)
  const handleAuthTransfer = useCallback(async (transferToken: string) => {
    // Guard against duplicate calls - the transfer token is one-time use
    if (authTransferInFlightRef.current) {
      console.log('[DEBUG:A] handleAuthTransfer skipped - already in flight')
      return
    }
    authTransferInFlightRef.current = true
    // #region agent log
    console.log('[DEBUG:A] handleAuthTransfer called', {hasTransferToken:!!transferToken,ts:Date.now()});
    // #endregion
    try {
      console.log('🔄 Processing auth transfer token...')
      const response = await fetch('/api/dotloop/auth/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ transferToken, fubUserId })
      })
      
      if (response.ok) {
        const result = await response.json()
        console.log('✅ Auth transfer successful:', result.dotloopEmail)
        // #region agent log
        console.log('[DEBUG:A,C] Auth transfer API succeeded, calling checkAuth', {email:result.dotloopEmail,hasIframeToken:!!result.iframeAuthToken,ts:Date.now()});
        // #endregion
        
        // Store the iframe auth token in localStorage for persistent auth in iframe contexts
        if (result.iframeAuthToken) {
          localStorage.setItem('dotloop_iframe_auth_token', result.iframeAuthToken)
        }
        
        // Refresh auth status after transfer
        await checkAuth()
      } else {
        console.error('❌ Auth transfer failed:', response.status)
        // Still try to check auth in case session-based auth worked
        await checkAuth()
      }
    } catch (error) {
      console.error('Auth transfer error:', error)
      // Still try to check auth in case session-based auth worked
      await checkAuth()
    } finally {
      authTransferInFlightRef.current = false
    }
  }, [checkAuth, fubUserId])

  // Check auth on mount and set up listeners for OAuth completion
  useEffect(() => {
    let isMounted = true

    const initializeAuth = async () => {
      const status = await checkAuth()
      const hasFubUserId = typeof fubUserId === 'number' && Number.isInteger(fubUserId) && fubUserId > 0

      if (!status.authenticated && hasFubUserId) {
        try {
          const reconnectResult = await dotloopApi.reconnect(fubUserId)
          if (reconnectResult?.authenticated && reconnectResult?.iframeAuthToken) {
            localStorage.setItem('dotloop_iframe_auth_token', reconnectResult.iframeAuthToken)
            if (isMounted) {
              await checkAuth()
            }
          }
        } catch (error) {
          console.warn('Reconnect attempt failed:', error)
        }
      }
    }

    initializeAuth()
    
    // Helper to process auth data from localStorage or postMessage
    const processAuthData = (data: { type?: string; transferToken?: string }) => {
      if (data?.type === 'DOTLOOP_AUTH_SUCCESS') {
        // #region agent log
        console.log('[DEBUG:A,B] DOTLOOP_AUTH_SUCCESS received, setting authPending=false', {hadTransferToken:!!data?.transferToken,authPendingBefore:authPendingRef.current,ts:Date.now()});
        // #endregion
        authPendingRef.current = false
        if (data?.transferToken) {
          handleAuthTransfer(data.transferToken)
        } else {
          console.log('🔄 Auth success without transfer token, checking auth...')
          checkAuth()
        }
      }
    }
    
    // Listen for localStorage changes (works reliably across same-origin windows)
    const handleStorage = (event: StorageEvent) => {
      // #region agent log
      console.log('[DEBUG:A] localStorage storage event', {key:event.key,hasNewValue:!!event.newValue,ts:Date.now()});
      // #endregion
      if (event.key === 'dotloop_auth_complete' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue)
          console.log('📬 Received auth completion via localStorage')
          processAuthData(data)
          // Clean up the localStorage item
          localStorage.removeItem('dotloop_auth_complete')
        } catch (e) {
          console.error('Failed to parse auth data from localStorage:', e)
        }
      }
    }
    
    // Listen for postMessage (fallback, works in some contexts)
    const handleMessage = (event: MessageEvent) => {
      // #region agent log
      console.log('[DEBUG:A] postMessage received', {type:event.data?.type,hasTransferToken:!!event.data?.transferToken,origin:event.origin,ts:Date.now()});
      // #endregion
      if (event.data?.type === 'DOTLOOP_AUTH_SUCCESS') {
        console.log('📬 Received postMessage:', event.data?.type)
        processAuthData(event.data)
      }
    }
    
    window.addEventListener('storage', handleStorage)
    window.addEventListener('message', handleMessage)
    
    // Check auth status every 5 minutes
    const interval = setInterval(checkAuth, 5 * 60 * 1000)
    
    return () => {
      isMounted = false
      clearInterval(interval)
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('message', handleMessage)
    }
  }, [handleAuthTransfer, checkAuth, fubUserId])

  const initiateAuth = useCallback(() => {
    // ALWAYS use switch flow - Dotloop remembers OAuth sessions even after our logout
    // This ensures users can always switch to a different account
    const forceSwitch = true
    
    // Clear any stale iframe auth token before starting new auth
    dotloopApi.clearIframeAuthToken()
    
    // Mark that we're expecting an auth callback
    authPendingRef.current = true
    authTransferInFlightRef.current = false

    // Create a client request id so the parent iframe can poll the server for completion.
    // This avoids relying on postMessage/localStorage across partitioned contexts.
    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes)
    const clientRequestId = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    authRequestIdRef.current = clientRequestId
    
    // Open OAuth in a popup window - larger to accommodate the switch flow UI
    const width = 600
    const height = 750
    const left = window.screen.width / 2 - width / 2
    const top = window.screen.height / 2 - height / 2
    
    const rawUrl = dotloopApi.getAuthUrl(forceSwitch, fubUserId)
    const url = new URL(rawUrl, window.location.origin)
    url.searchParams.set('client_request_id', clientRequestId)

    const popup = window.open(
      url.toString(),
      'DotLoop Authentication',
      `width=${width},height=${height},left=${left},top=${top}`
    )

    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      alert('Pop-up blocked! Please allow pop-ups for this site to authenticate with DotLoop.')
      authPendingRef.current = false
      return
    }

    // Poll to check when authentication is complete.
    // The SERVER POLL is the primary mechanism — it works reliably in iframe/
    // partitioned-storage contexts where localStorage and postMessage do not.
    // Crucially, we keep polling even after the popup closes because auth may
    // have completed on the server but we haven't picked it up yet.
    let checkCount = 0
    const maxChecks = 240 // 120 seconds max (500ms * 240)
    let popupClosedAtCheck = -1 // track when we first noticed the popup closed
    const POST_CLOSE_CHECKS = 30 // keep polling 15s after popup closes (500ms * 30)
    
    const checkPopup = setInterval(async () => {
      checkCount++

      // --- Primary: poll the server for completion ---
      if (authPendingRef.current && authRequestIdRef.current) {
        try {
          const response = await fetch(`/api/dotloop/auth/request-status?client_request_id=${encodeURIComponent(authRequestIdRef.current)}`, {
            credentials: 'include'
          })
          if (response.ok) {
            const data = await response.json()
            // #region agent log
            console.log('[DEBUG:B] request-status poll', {completed:!!data?.completed,popupClosed:popup.closed,checkCount,ts:Date.now()});
            // #endregion
            if (data?.completed && data?.transferToken) {
              // #region agent log
              console.log('[DEBUG:B] request-status completed -> handleAuthTransfer', {ts:Date.now()});
              // #endregion
              authPendingRef.current = false
              clearInterval(checkPopup)
              handleAuthTransfer(data.transferToken)
              try { popup.close() } catch (_) {}
              return
            }
          }
        } catch (e) {
          // ignore - will retry next tick
        }
      }

      // --- Secondary: poll localStorage (works in same-origin, non-partitioned contexts) ---
      if (authPendingRef.current) {
        try {
          const authData = localStorage.getItem('dotloop_auth_complete')
          if (authData) {
            // #region agent log
            console.log('[DEBUG:B] Found auth data in localStorage while polling', {checkCount,ts:Date.now()});
            // #endregion
            const data = JSON.parse(authData)
            if (data?.type === 'DOTLOOP_AUTH_SUCCESS' && data?.transferToken) {
              console.log('📬 Found auth completion in localStorage (polling)')
              authPendingRef.current = false
              clearInterval(checkPopup)
              localStorage.removeItem('dotloop_auth_complete')
              handleAuthTransfer(data.transferToken)
              try { popup.close() } catch (_) {}
              return
            }
          }
        } catch (e) {
          // ignore
        }
      }

      // --- Track popup closure, but DON'T stop polling immediately ---
      if (popup.closed && popupClosedAtCheck < 0) {
        popupClosedAtCheck = checkCount
        // #region agent log
        console.log('[DEBUG:B] Popup closed detected, continuing server poll', {authPending:authPendingRef.current,checkCount,ts:Date.now()});
        // #endregion
      }
      
      // --- Stop conditions ---
      const postCloseExhausted = popupClosedAtCheck >= 0 && (checkCount - popupClosedAtCheck) >= POST_CLOSE_CHECKS
      if (postCloseExhausted || checkCount >= maxChecks) {
        clearInterval(checkPopup)
        
        if (authPendingRef.current) {
          // #region agent log
          console.log('[DEBUG:B] Polling exhausted, final checkAuth fallback', {reason: postCloseExhausted ? 'popup_closed_timeout' : 'max_checks', checkCount, ts:Date.now()});
          // #endregion
          authPendingRef.current = false
          // Last resort: plain checkAuth (works if session cookies are available)
          checkAuth()
        }
      }
    }, 500)
  }, [checkAuth, handleAuthTransfer, fubUserId])

  // Alternative auth method: full-page redirect instead of popup
  // Use this if popup-based auth still triggers Chrome Safe Browsing warnings
  const initiateAuthRedirect = useCallback(() => {
    // Clear any stale iframe auth token before starting new auth
    dotloopApi.clearIframeAuthToken()
    
    // Use the switch flow URL for full-page redirect
    const url = dotloopApi.getAuthUrl(true, fubUserId)
    
    // Full page redirect - leaves the current page and navigates to OAuth
    // After auth completes, user will be redirected back to the app
    window.location.href = url
  }, [fubUserId])

  const logout = useCallback(async (): Promise<void> => {
    const iframeAuthToken = localStorage.getItem('dotloop_iframe_auth_token')
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (iframeAuthToken) {
      headers['X-Iframe-Auth-Token'] = iframeAuthToken
    }

    try {
      await fetch('/api/dotloop/logout', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          fubUserId: typeof fubUserId === 'number' && Number.isInteger(fubUserId) && fubUserId > 0 ? fubUserId : null
        })
      })
      // Reset auth status
      setAuthStatus(defaultAuthStatus)
    } catch (error) {
      console.error('Error logging out:', error)
    } finally {
      // Clear iframe auth token from localStorage after logout request
      dotloopApi.clearIframeAuthToken()
    }
  }, [fubUserId])

  return {
    authStatus,
    loading,
    checkAuth,
    initiateAuth,
    initiateAuthRedirect,  // Full-page redirect alternative (avoids popup)
    logout,
    isAuthenticated: authStatus.authenticated,
    needsReauth: authStatus.needsReauth,
    validationError: authStatus.validationError,
    dotloopEmail: authStatus.dotloopEmail
  }
}

